import React, { useState } from "react";
import { toast } from "../hooks/use-toast";
import Reveal from "./Reveal";
import { Sparkles, Snowflake, Phone, ExternalLink } from "lucide-react";
import { guide } from "../mock";

// ============================================================================
// Google Form integration — PASTE YOUR NEW FORM ID + ENTRY IDS BELOW
// ----------------------------------------------------------------------------
// To wire up a new simple Google Form (Name, Email, Phone, Roommate, Message):
//   1. Create the Google Form with those 5 fields
//   2. Click "Get pre-filled link" in the 3-dot menu → fill each field with a
//      placeholder value → click "Get link" → copy each entry.XXXXXXXXX from URL
//   3. Replace GFORM_ID and each entry ID below
// ============================================================================
const GFORM_ID = "1FAIpQLScB8E2iUPhAFIOV4JgTpBqWZb8ml6L-t-q-brb1PplC2NBl3Q";
const GFORM_ACTION = `https://docs.google.com/forms/d/e/${GFORM_ID}/formResponse`;
const GFORM_VIEW = `https://docs.google.com/forms/d/e/${GFORM_ID}/viewform`;

// Field IDs from Google Form: "Ladies' Christmas Market Tour Wishlist"
const FIELDS = {
  firstName: "entry.1120157391",  // First Name *
  lastName: "entry.2092238618",   // Last Name *
  email: "entry.479301265",       // Email *
  phone: "entry.1272029862",      // Phone Number *
  roommate: "entry.1753222212",   // Roommate Preference *
  message: "entry.1420671725",    // Anything we should know?
};

const Reserve = () => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", roommate: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      toast({ title: "Almost there", description: "First name, last name and email are required." });
      return;
    }
    if (!form.phone || !form.roommate) {
      toast({ title: "One more thing", description: "Phone and roommate preference are required." });
      return;
    }
    setSubmitting(true);

    // POST to Google Forms
    const data = new FormData();
    data.append(FIELDS.firstName, form.firstName);
    data.append(FIELDS.lastName, form.lastName);
    data.append(FIELDS.email, form.email);
    data.append(FIELDS.phone, form.phone);
    data.append(FIELDS.roommate, form.roommate);
    if (form.message) data.append(FIELDS.message, form.message);

    fetch(GFORM_ACTION, { method: "POST", mode: "no-cors", body: data })
      .catch(() => {})
      .finally(() => {
        const existing = JSON.parse(localStorage.getItem("btp_reservations") || "[]");
        existing.push({ ...form, createdAt: new Date().toISOString() });
        localStorage.setItem("btp_reservations", JSON.stringify(existing));

        setSubmitting(false);
        setForm({ firstName: "", lastName: "", email: "", phone: "", roommate: "", message: "" });
        toast({
          title: "Your spot request was sent! ✨",
          description: "Sophia will reach out within 48 hours with next steps and the $200 deposit link.",
        });
      });
  };

  return (
    <section id="reserve" className="section bg-paper relative">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-12">
            <Snowflake className="w-10 h-10 text-[#c9a55c] mx-auto mb-3" strokeWidth={1} />
            <span className="font-script text-[#c9a55c] text-3xl block">join us</span>
            <h2 className="font-display text-[#1a3a2e] text-4xl md:text-5xl mt-1 mb-3">
              Reserve Your <em className="italic text-[#c9a55c]">Spot</em>
            </h2>
            <p className="text-[#3b463f]/80 max-w-xl mx-auto font-light">
              Spots are limited — this is a small-group trip. Secure yours with a $200 non-refundable registration fee — this holds your place but is <em>separate</em> from the $2,695 trip cost, which is due in full by August 29, 2026.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mb-10 bg-[#1a3a2e] rounded-sm p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 sm:gap-8 text-center sm:text-left">
            <div className="w-14 h-14 rounded-full bg-[#c9a55c] flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-[#1a3a2e]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-[#faf5ec] text-2xl mb-1">Prefer to text first?</h3>
              <p className="text-[#faf5ec]/80 text-sm font-light">
                Got questions before you commit? Shoot Sophia a quick text — she usually replies within a few hours.
              </p>
            </div>
            <a
              href="sms:+18188254308?&body=Hi Sophia! I'm interested in the Christmas Markets trip — can you send me more details?"
              className="bg-[#c9a55c] hover:bg-[#e9c987] text-[#1a3a2e] px-6 py-3 text-[12px] tracking-[0.18em] uppercase font-semibold rounded-sm inline-flex items-center gap-2 transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              Text 818-825-4308
            </a>
          </div>
        </Reveal>

        <Reveal>
          <form
            onSubmit={handleSubmit}
            className="bg-[#fdfaf2] border border-[#1a3a2e]/10 p-8 sm:p-10 rounded-sm shadow-[0_30px_60px_-30px_rgba(26,58,46,0.2)] grid sm:grid-cols-2 gap-5"
          >
            <div className="sm:col-span-1">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#1a3a2e] font-medium block mb-2">
                First Name <span className="text-[#8b3a3a]">*</span>
              </label>
              <input
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                className="w-full bg-[#faf5ec] border border-[#1a3a2e]/15 px-4 py-3 text-[#1a3a2e] focus:outline-none focus:border-[#c9a55c] focus:ring-1 focus:ring-[#c9a55c] transition-colors rounded-sm"
                placeholder="Jane"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#1a3a2e] font-medium block mb-2">
                Last Name <span className="text-[#8b3a3a]">*</span>
              </label>
              <input
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                className="w-full bg-[#faf5ec] border border-[#1a3a2e]/15 px-4 py-3 text-[#1a3a2e] focus:outline-none focus:border-[#c9a55c] focus:ring-1 focus:ring-[#c9a55c] transition-colors rounded-sm"
                placeholder="Doe"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#1a3a2e] font-medium block mb-2">
                Email <span className="text-[#8b3a3a]">*</span>
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full bg-[#faf5ec] border border-[#1a3a2e]/15 px-4 py-3 text-[#1a3a2e] focus:outline-none focus:border-[#c9a55c] focus:ring-1 focus:ring-[#c9a55c] transition-colors rounded-sm"
                placeholder="jane@email.com"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#1a3a2e] font-medium block mb-2">
                Phone <span className="text-[#8b3a3a]">*</span>
              </label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                className="w-full bg-[#faf5ec] border border-[#1a3a2e]/15 px-4 py-3 text-[#1a3a2e] focus:outline-none focus:border-[#c9a55c] focus:ring-1 focus:ring-[#c9a55c] transition-colors rounded-sm"
                placeholder="(555) 555-5555"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#1a3a2e] font-medium block mb-2">
                Roommate Preference <span className="text-[#8b3a3a]">*</span>
              </label>
              <input
                name="roommate"
                value={form.roommate}
                onChange={handleChange}
                className="w-full bg-[#faf5ec] border border-[#1a3a2e]/15 px-4 py-3 text-[#1a3a2e] focus:outline-none focus:border-[#c9a55c] focus:ring-1 focus:ring-[#c9a55c] transition-colors rounded-sm"
                placeholder="Solo, friend's name, etc."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#1a3a2e] font-medium block mb-2">
                Anything we should know?
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                className="w-full bg-[#faf5ec] border border-[#1a3a2e]/15 px-4 py-3 text-[#1a3a2e] focus:outline-none focus:border-[#c9a55c] focus:ring-1 focus:ring-[#c9a55c] transition-colors rounded-sm resize-none"
                placeholder="Dietary needs, questions, excitement levels..."
              />
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="text-[#3b463f]/70 text-xs sm:text-sm font-light flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#c9a55c]" strokeWidth={1.5} />
                You&rsquo;ll receive a confirmation email & deposit link within 48h.
              </p>
              <button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? "Sending..." : "Reserve My Spot"}
              </button>
            </div>
            <p className="sm:col-span-2 text-center text-[11px] text-[#3b463f]/60 font-light mt-2">
              Having trouble?{" "}
              <a href={GFORM_VIEW} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1a3a2e] underline decoration-[#c9a55c] underline-offset-4 hover:text-[#c9a55c]">
                Open the Google Form directly <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              </a>
            </p>
          </form>
        </Reveal>

        <Reveal>
          <p className="text-center mt-10 text-[#3b463f]/70 text-sm">
            Questions? Email{" "}
            <a href={`mailto:${guide.email}`} className="text-[#1a3a2e] underline decoration-[#c9a55c] underline-offset-4 hover:text-[#c9a55c]">
              {guide.email}
            </a>{" "}
            or call {guide.phone}.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Reserve;
